class StorageAdapter {
	/** @param {Map|Storage|StorageAdapter} store */
	constructor( store ) {
		Object.assign( this, { store } );
	}

	clear() {
		this.store.clear();
	}

	delete( key ) {
		const { store } = this;
		( store.removeItem || store.delete ).call( store, key );
	}

	get( key ) {
		const { store } = this;
		if( typeof store.getItem === 'function' ) {
			const retval = store.getItem( key );
			if( retval === null ) return undefined;
			return retval;
		} else {
			return store.get( key );
		}
	}

	set( key, value ) {
		const { store } = this;
		( store.setItem || store.set ).call( store, key, value );
	}
}

export class LookupCache {
	/**
	 * @param {Function} fn
	 * @param {Map|Storage|StorageAdapter} [store]
	 */
	constructor( fn, store = new Map ) {
		Object.assign( this, { fn, store: new StorageAdapter( store ) } );
	}

	clear() {
		this.store.clear();
	}

	delete( key ) {
		this.store.delete( key );
	}

	get( key ) {
		const { store, fn } = this,
			currentValue = store.get( key );
		if( currentValue !== undefined ) return currentValue;
		const value = fn();
		this.set( key, value );
		return value;
	}

	set( key, value ) {
		this.store.set( key, value );
	}
}

export class LookupCacheAsync {
	/**
	 * @param {Function} fn
	 * @param {Map|Storage|StorageAdapter} [resolved]
	 */
	constructor( fn, resolved = new Map ) {
		const store = new Map;
		Object.assign( this, { fn, store, resolved: new StorageAdapter( resolved ) } );
	}

	clear() {
		const { store, resolved } = this;
		store.clear();
		resolved.clear();
	}

	delete( key ) {
		const { store, resolved } = this;
		store.delete( key );
		resolved.delete( key );
	}

	get( key ) {
		const { fn, store, resolved } = this;
		const currentValue = resolved.get( key );
		if( typeof currentValue === 'string' ) {
			return JSON.parse( currentValue );
		}
		let promise = store.get( key );
		if( promise === undefined ) {
			promise = Promise.resolve( fn( key ) );
		} else {
			promise =
			promise
			.then( undefined, () => Promise.resolve( fn( key ) ) ); // if previous request failed, try again
		}
		this.set( key, promise );
		return promise;
	}

	set( key, value ) {
		const { store, resolved } = this;
		const promise = Promise.resolve( value );
		store.set( key, promise );
		promise.then( value => {
			if( store.get( key ) === promise ) { // deal with race conditions or key deleted before resolve
				resolved.set( key, JSON.stringify( value ) );
			}
		} );
	}
}

export function asyncDecorator( origFn, onlyLatest = false ) {
	const q = [];
	let p = Promise.resolve();
	return function( ...args ) {
		const self = this;
		const retval = new Promise( ( resolve, reject ) => {
			if( onlyLatest ) {
				for( const r of q.splice( 0, q.length ) ) {
					r.reject( new Error( 'Superceded by later call' ) );
				}
			}

			q.push( {
				fn: () => {
					const retval = origFn.call( self, ...args ) || Promise.resolve();
					retval.then( resolve, reject );
					return retval;
				},
				resolve,
				reject
			} );

			p = p.then( () => { }, () => { } ).then( () => {
				const n = q.shift();
				if( n ) return n.fn();
			} );
		} );
		retval.then( null, () => {} ); // don't print unhandled error messages
		return retval;
	};
}
